import get from 'lodash/get'
import sessionStorage from 'sessionstorage'
import { FlyApiConfig } from '../api-config'
import { ErrorInfoObj } from '../FlyAPICommon/error-info-model'
import { CommonApiService } from '../FlyAPIUtil/pcln-common-api-service'
import { CommonFunctionUtil } from '../FlyAPIUtil/common-util-factory'
import { v4 as uuidv4 } from 'uuid'
import { ReferralInfoObj } from '../FlyAPICommon/referral-info-model'
import { SliceInfoObj } from '../FlyAPICommon/slice-info-model'
import { shouldPerformSessionKeySearch } from '../SessionKeyHelper/session-key-helper'
import { itineraryTypes } from '../../common/constants/itinerary'
import { FlySearchResponseObj } from './response-model'
import { ExpressDealInfoObj } from './express-deal-info-model'

class FlySearchRetailService {
  constructor() {
    this.CONFIG = FlyApiConfig.flySearch
  }

  /**
   * Getter method to call the Fly Search API to get the list of all itineraries available
   */
  execute(requestObj) {
    return new Promise((resolve, reject) => {
      const params = requestObj.getParams()
      const rawRequest = this.buildRequest(requestObj)

      CommonApiService.execute(rawRequest, this.CONFIG, params).then(
        rsp => {
          const errorObj = new ErrorInfoObj()
          errorObj.parse(rsp, this.CONFIG.responseName)

          if (errorObj.isError()) {
            reject(errorObj)
          } else {
            const responseObj = this.processResponse(rsp)

            resolve(responseObj)
          }
        },
        err => {
          const errorObj = new ErrorInfoObj()
          errorObj.parse(err, this.CONFIG.responseName)

          reject(errorObj)
        }
      )
    })
  }

  /**
   * Builds raw-request out of request object of FlySearchRequestObj type
   * If the object is not passed-in, return empty json
   * @param requestObj
   * @returns {{}}
   * @public
   */
  buildRequest(
    requestObj,
    SETI = {},
    sessKeySearchFailed,
    origSliceFilters = []
  ) {
    const { ROUND_TRIP } = itineraryTypes
    const rawRequest = {}
    let requestHandle = {
      searchOptions: {},
    }
    const excludedCarriers = []
    const reqItineraryType = requestObj.getItineraryType()
    const isSBS = reqItineraryType === ROUND_TRIP
    const isFilteringBySliceKey = origSliceFilters.some(filter =>
      filter.hasOwnProperty('sliceKey')
    )
    const isDepartSBS = isSBS && !isFilteringBySliceKey
    const isReturnSBS = isSBS && isFilteringBySliceKey

    if (requestObj) {
      const willMakeAirSearchDisplayRequest =
        !sessKeySearchFailed &&
        (requestObj.getSearchSessionKey() || shouldPerformSessionKeySearch())

      const upperBound = requestObj.getUpperBound()

      if (willMakeAirSearchDisplayRequest) {
        rawRequest.airSearchDisplayReq = {}
        requestHandle = rawRequest.airSearchDisplayReq

        requestHandle.requestId = uuidv4()
        requestHandle.clientSessionId = uuidv4()
        requestHandle.searchSessionKey = requestObj.getSearchSessionKey()
        if (!requestHandle.searchSessionKey) {
          requestHandle.searchSessionKey = sessionStorage.getItem(
            'searchSessionKey'
          )
        }
        requestHandle.searchId = requestObj.getSearchId()

        if (requestObj.getPresentationInterface()) {
          requestHandle.presentationInterface = requestObj.getPresentationInterface()
        }

        requestHandle.displayParameters = {
          sliceRefId: [],
          lowerBound: requestObj.getLowerBound(),
          upperBound: upperBound,
          includeExpressDeals: true,
        }

        if (requestObj.getSliceFilters()) {
          requestHandle.filter = { sliceFilters: requestObj.getSliceFilters() }
        }

        if (requestObj.getItineraryType()) {
          requestHandle.itineraryType = requestObj.getItineraryType()
        }

        requestHandle.searchOptions = this.getRequestSearchOptions({
          requestObj,
          SETI,
        })

        if (requestObj.i18n) {
          requestHandle.i18n = {
            displayCurrencyCode: requestObj.i18n.displayCurrencyCode || 'USD',
            includeTransactionalCurrency:
              requestObj.i18n.includeTransactionalCurrency || true,
          }
        }
      } else {
        rawRequest.airSearchReq = {}
        requestHandle = rawRequest.airSearchReq

        requestHandle.requestId = uuidv4()
        requestHandle.clientSessionId =
          requestObj.getClientSessionId() || uuidv4()
        requestHandle.trip = {
          disclosure: 'DISCLOSED',
          slice: [],
        }

        if (requestObj.isExpressDealRequired()) {
          requestHandle.expressDeal = {
            slice: [],
            openJawAllowed: true,
            discloseSeatSelectionCarryOnBag: true,
          }

          requestHandle.expressDeal.allowedAlternates = []
          if (requestObj.isAlternateAirportsAllowedExpDeal()) {
            requestHandle.expressDeal.allowedAlternates.push('AIRPORT')
          }

          if (requestObj.isAlternateDatesAllowedExpDeal()) {
            requestHandle.expressDeal.allowedAlternates.push('DATE')
          }
        }

        if (requestObj.i18n) {
          requestHandle.i18n = {
            displayCurrencyCode: requestObj.i18n.displayCurrencyCode || 'USD',
            includeTransactionalCurrency:
              requestObj.i18n.includeTransactionalCurrency || true,
          }
        }

        requestObj.getSliceData().forEach(slice => {
          const rawSlice = {
            id: requestHandle.trip.slice.length + 1,
            departDate: slice.getDepartDate(),
            origins: [],
            destinations: [],
          }

          const originAirportType = slice.getOriginAirportType()
          slice.getOriginAirport().forEach((originAirport, index) => {
            rawSlice.origins.push({
              location: originAirport,
              type: originAirportType[index],
            })
          })
          const destinationAirportType = slice.getDestinationAirportType()
          slice.getDestinationAirport().forEach((destinationAirport, index) => {
            rawSlice.destinations.push({
              location: destinationAirport,
              type: destinationAirportType[index],
            })
          })
          if (requestObj.isAlternateDatesAllowed()) {
            rawSlice.alternateDaysBefore = slice.getAlternateDaysBefore()
            rawSlice.alternateDaysAfter = slice.getAlternateDaysAfter()
          }

          requestHandle.trip.slice.push(rawSlice)
          if (requestObj.isExpressDealRequired()) {
            requestHandle.expressDeal.slice.push(rawSlice)
          }
        })

        requestHandle.passenger = []

        // No need for a check on number of Adults present, since ADT is the only one supported for all types
        if (
          requestObj.getNumAdults() ||
          requestObj.getNumChildren() ||
          requestObj.getNumSeniors()
        ) {
          requestHandle.passenger.push({
            type: 'ADT',
            numOfPax:
              requestObj.getNumAdults() +
              requestObj.getNumChildren() +
              requestObj.getNumSeniors(),
          })
        }

        const numOfStops = requestObj.getFilterNumOfStops()
        const newSliceFilter = requestObj.getSliceFilters()
          ? requestObj.getSliceFilters()
          : []
        if (!requestObj.getSliceFilters()) {
          const sliceLength = requestObj.getSliceData().length
          for (let i = 1; i <= sliceLength; i++) {
            newSliceFilter.push({
              sliceId: i,
              numOfStops,
              stopsFilterType: 'MAX',
              excludedCarriers,
            })
          }
        } else {
          newSliceFilter.forEach((sliceFilter, index) => {
            sliceFilter.sliceId = index + 1
            sliceFilter.numOfStops = numOfStops
            sliceFilter.stopsFilterType = 'MAX'

            if (!sliceFilter.excludedCarriers) {
              sliceFilter.excludedCarriers = []
            }
          })
        }
        requestObj.setSliceFilters(newSliceFilter)
        if (!requestHandle.filter) {
          requestHandle.filter = {}
        }
        requestHandle.filter.sliceFilters = requestObj.getSliceFilters()

        requestHandle.searchOptions = this.getRequestSearchOptions({
          requestObj,
          SETI,
        })

        if (requestObj.isCacheOnlySearch()) {
          requestHandle.searchOptions.cacheOptions = {
            cacheOnly: true,
            discardSessionData: true,
            ageOfCacheInMinutes: 1440,
          }
        }

        requestHandle.liveSearchForAlternateDates = requestObj.getLiveSearchForAlternateDates()

        requestHandle.liveSearchExpDealsForAlternateDates = requestObj.getLiveSearchExpDealsForAlternateDates()

        requestHandle.displayParameters = {
          sliceRefId: [],
          lowerBound: requestObj.getLowerBound() || 1,
          upperBound: upperBound || 40,
        }

        if (requestObj.isFilterDirectionOutbound()) {
          requestHandle.displayParameters.sliceRefId.push(
            requestHandle.trip.slice[0].id
          )
        } else {
          requestHandle.trip.slice.forEach(slice => {
            requestHandle.displayParameters.sliceRefId.push(slice.id)
          })
        }

        requestHandle.requestBasicEconomy = true
      }

      if (isDepartSBS) {
        requestHandle.displayParameters.sliceRefId = [
          get(requestHandle, 'trip.slice[0].id', 1),
        ]
      } else if (isReturnSBS) {
        requestHandle.displayParameters.sliceRefId = [
          get(requestHandle, 'trip.slice[1].id', 2),
        ]
      }

      requestHandle.filter = requestHandle.filter || {}
      if (origSliceFilters.length) {
        requestHandle.filter.sliceFilters = origSliceFilters
      }

      requestHandle.filter.alternates = {
        excludeDates: !requestObj.isAlternateDatesAllowed(),
        excludeAirports: !requestObj.isAlternateAirportsAllowed(),
      }

      const exactDateArray = requestObj.getExactDateFilters()
      if (exactDateArray && exactDateArray.length > 0) {
        requestHandle.filter.exactDateFilters = requestObj.getExactDateFilters()
      }

      // TODO: Make this conversion better
      requestHandle.sortPrefs = requestObj.sortPreferences

      requestHandle.includeAlternateSummary =
        requestObj.isAlternateDatesAllowed() ||
        requestObj.isAlternateAirportsAllowed()
      requestHandle.includeFullTripSummary = requestObj.isIncludeFullTripSummary()
      requestHandle.includeFilteredTripSummary = requestObj.isIncludeFilteredTripSummary()
      requestHandle.includeSliceSummary = requestObj.isIncludeSliceSummary()
      requestHandle.advDuplicateTimesItinFilter = requestObj.isAdvDuplicateTimesItinFilter()
      requestHandle.advSameDepartureItinFilter = requestObj.isAdvSameDepartureItinFilter()
      requestHandle.advSimDepartSimDurationItinFilter = requestObj.isAdvSimDepartSimDurationItinFilter()
      requestHandle.responseControl = requestObj.getResponseControl()
      requestHandle.mergeItineraryList = true
      requestHandle.includeFareBasisCode = true

      requestHandle.referrals = []
      requestHandle.referrals.push(new ReferralInfoObj().get())
    }

    return rawRequest
  }

  processResponse(rawResponseRoot) {
    const CONFIG = FlyApiConfig.flySearch
    const rawResponse = rawResponseRoot[CONFIG.responseName]
    const responseObj = new FlySearchResponseObj()
    responseObj.setRawResponse(rawResponse)
    responseObj.setClientSessionId(rawResponse.clientSessionId)
    responseObj.setItineraryTypeCode(rawResponse.itineraryType)

    const pricedItineraries = new Map()

    rawResponse.pricedItinerary =
      rawResponse.pricedItinerary || rawResponse.itineraries

    let expressDealCtr = 0
    rawResponse.pricedItinerary.forEach(pricedItin => {
      pricedItin.isExpressDeal = !!pricedItin.candidateKey

      const pricingInfo = pricedItin.pricingInfo
      const key =
        pricedItin.candidateKey ||
        pricingInfo.itineraryReference.token ||
        pricingInfo.itineraryReference.refId

      if (pricedItin.isExpressDeal) {
        const expressDealInfoObj = new ExpressDealInfoObj()
        // There is no need for this flag permanently, however patching it for now.
        expressDealInfoObj.isExpressDeal = true
        expressDealInfoObj.setId(pricedItin.id)
        expressDealInfoObj.setCandidateKey(pricedItin.candidateKey)
        expressDealInfoObj
          .getItineraryReferenceInfo()
          .update(pricedItin.itineraryReference)
        expressDealInfoObj.getPricingInfo().update(pricedItin.pricingInfo)
        expressDealInfoObj.setSameDayDepartHoursBeforeTravel(
          pricedItin.sameDayDepartMinsBeforeTravelDial
        )
        expressDealInfoObj.setOpenJaw(pricedItin.openJaw)
        expressDealInfoObj.setIsSaleEligible(!!pricedItin.isSaleEligible)
        pricedItin.slice.forEach(rawSlice => {
          const sliceObj = new SliceInfoObj()
          sliceObj.update(rawSlice)
          expressDealInfoObj.addSlice(sliceObj)
        })

        pricedItineraries.set(key + expressDealCtr, expressDealInfoObj)
        expressDealCtr++
      } else {
        // Code for handling void window
        pricingInfo.voidWindowInfo = null
        if (pricedItin.isFused && pricingInfo.componentInformationList) {
          const componentList = pricingInfo.componentInformationList
          // TODO: Remove this if statement eventually. Added temporarily due to AIR-8791
          if (componentList && componentList.length > 0) {
            let splitPreviousVoidWindow = 0
            for (let index = 0; index < componentList.length; index++) {
              const component = componentList[index]
              if (!component.voidWindowClose) {
                pricingInfo.voidWindowInfo = null
                break
              } else {
                // try block is added to recover in case api sends void window in wrong format
                try {
                  if (
                    splitPreviousVoidWindow === 0 ||
                    splitPreviousVoidWindow >
                      new Date(component.voidWindowClose)
                  ) {
                    pricingInfo.voidWindowInfo = this.getVoidWindowInfo(
                      component.voidWindowClose,
                      component.voidWindowCloseTZDesignator
                    )
                    splitPreviousVoidWindow = new Date(
                      component.voidWindowClose
                    )
                  }
                } catch (err) {
                  pricingInfo.voidWindowInfo = null
                  break
                }
              }
            }
          }
        } else {
          if (pricingInfo.voidWindowClose) {
            pricingInfo.voidWindowInfo = this.getVoidWindowInfo(
              pricingInfo.voidWindowClose,
              pricingInfo.voidWindowCloseTZDesignator
            )
          }
        }

        pricedItineraries.set(key, pricedItin)
      }
    })

    responseObj.pricedItineraryTable = pricedItineraries

    // Cleanup duplicate data
    delete rawResponse.itineraries
    delete rawResponse.pricedItinerary

    const slices = new Map()

    rawResponse.slice.forEach(slice => {
      slices.set(slice.uniqueSliceId, slice)
    })

    responseObj.sliceTable = slices

    const segments = new Map()

    if (rawResponse.segment) {
      rawResponse.segment.forEach(segment => {
        segments.set(segment.uniqueSegId, segment)
      })
    }

    responseObj.segmentTable = segments

    const airports = new Map()

    rawResponse.airport.forEach(airport => {
      airports.set(airport.code, airport)
    })

    responseObj.airportTable = airports

    const airlines = new Map()

    rawResponse.airline.forEach(airline => {
      airlines.set(airline.code, airline)
    })

    responseObj.airlineTable = airlines

    const equipments = new Map()

    rawResponse.equipment.forEach(equipment => {
      equipments.set(equipment.code, equipment)
    })

    responseObj.equipmentTable = equipments

    const filterableSliceData = new Map()

    rawResponse.sliceSummary.forEach(sliceSummary => {
      filterableSliceData.set(sliceSummary.sliceRefId, sliceSummary)
    })

    responseObj.filterableSliceTable = filterableSliceData

    const filterableAirports = new Map()

    const filterableAirlines = new Map()

    const originMap = new Map()

    const destMap = new Map()

    rawResponse.totalTripSummary.carrier.forEach(carrier => {
      filterableAirlines.set(carrier.code, airlines.get(carrier.code))
      carrier.airport.forEach(airport => {
        originMap.set(airport.origAirport, airport.origAirport)
        destMap.set(airport.destAirport, airport.destAirport)
      })
    })

    const originArray = [...originMap].map(elem => elem[0])

    const destArray = [...destMap].map(elem => elem[0])

    const sliceIds = [
      {
        originAirports: originArray,
        destinationAirport: destArray,
      },
      {
        originAirports: destArray,
        destinationAirport: originArray,
      },
    ]

    // Represents a slice ID with
    // 1 is outgoing
    // 2 returning
    // so on is multi destination
    // Todo speak to air back propogate properly
    filterableAirports.set(1, sliceIds[0])
    filterableAirports.set(2, sliceIds[1])

    responseObj.filterableAirportsTable = filterableAirports
    responseObj.filterableAirlinesTable = filterableAirlines

    const sliceSummary = new Map()
    sliceSummary.set('sliceSummary', rawResponse.sliceSummary)
    responseObj.sliceSummaryTable = sliceSummary

    // Code specific to express Deal
    if (rawResponse.expressDeal) {
      // Creates Candidates here only when there are separate lists for retail and express deal
      if (rawResponse.expressDeal.candidates) {
        let sliceObj
        let expressDealInfoObj
        rawResponse.expressDeal.candidates.forEach(rawExpressDealInfo => {
          expressDealInfoObj = new ExpressDealInfoObj()
          expressDealInfoObj.setId(rawExpressDealInfo.id)
          expressDealInfoObj.setCandidateKey(rawExpressDealInfo.candidateKey)
          expressDealInfoObj
            .getItineraryReferenceInfo()
            .update(rawExpressDealInfo.itineraryReference)
          expressDealInfoObj
            .getPricingInfo()
            .update(rawExpressDealInfo.pricingInfo)
          expressDealInfoObj.setSameDayDepartHoursBeforeTravel(
            rawExpressDealInfo.sameDayDepartMinsBeforeTravelDial
          )
          expressDealInfoObj.setOpenJaw(rawExpressDealInfo.openJaw)
          expressDealInfoObj.setIsSaleEligible(
            !!rawExpressDealInfo.isSaleEligible
          )
          rawExpressDealInfo.slice.forEach(rawSlice => {
            sliceObj = new SliceInfoObj()
            sliceObj.update(rawSlice)
            expressDealInfoObj.addSlice(sliceObj)
          })
          responseObj.addExpressDealInfo(expressDealInfoObj)
        })

        // Sort express deal by price
        responseObj.getExpressDealInfo().sort((a, b) => {
          return (
            a.getPricingInfo().getTotalFare() -
            b.getPricingInfo().getTotalFare()
          )
        })
      }

      if (
        rawResponse.expressDeal.displayableCarriers &&
        rawResponse.expressDeal.displayableCarriers.length > 0
      ) {
        responseObj.setExpressDealDisplayableCarriers(
          rawResponse.expressDeal.displayableCarriers
        )
      }
    }

    return responseObj
  }

  getVoidWindowInfo(timeStamp, tzDesignator) {
    if (timeStamp && tzDesignator) {
      const clientDate = new Date()
      const clientUTCDate = `${clientDate.getUTCFullYear()}-${this._p2(
        clientDate.getUTCMonth() + 1
      )}-${this._p2(clientDate.getUTCDate())}`
      const clientUTCTime = `${this._p2(clientDate.getUTCHours())}:${this._p2(
        clientDate.getUTCMinutes()
      )}:
        ${this._p2(clientDate.getUTCSeconds())}`
      const clientUTCDateTime = `${clientUTCDate}T${clientUTCTime}`

      return {
        timeStamp,
        tzDesignator,
        cancelTimeZone: CommonFunctionUtil.getTimeZone(tzDesignator),
        clientUTCDateTime,
      }
    }
    return null
  }

  getRequestSearchOptions({ requestObj, SETI }) {
    return {
      preferredCarriers: requestObj.getFilterPreferredCarriers(),
      excludedCarriers: requestObj.getFilterExcludedCarriers(),
      jetOnly: requestObj.isFilterJetOnly(),
      cabinClass: this.getCabinClass({ requestObj, SETI }),
      includeFusedItineraries: requestObj.isIncludeFusedItineraries(),
      maxLayoverDurationInMinutes: requestObj.maxLayoverDurationInMinutes,
      stopsFilterType: requestObj.getStopsFilterType,
    }
  }

  getCabinClass({ requestObj }) {
    return requestObj.getFilterCabinClass()
  }

  _p2(number) {
    return (number < 10 ? '0' : '') + number
  }
}

const flySearchRetailService = new FlySearchRetailService()

export { flySearchRetailService as FlySearchRetailService }
